// Conversion between our RichText format and TipTap/ProseMirror JSON.
// This is the bridge between backend storage (RichText[]) and editor state (ProseMirror doc).

import type { RichText } from '@core/types/document';
import type { JSONContent } from '@tiptap/react';

/**
 * Convert our RichText[] array to TipTap JSONContent nodes (inline content for a single block).
 * Returns an array of text nodes with marks.
 */
export function richTextToTipTapContent(richText: RichText[]): JSONContent[] {
  if (!richText || richText.length === 0) return [];

  return richText.map((rt) => {
    const marks: JSONContent['marks'] = [];

    if (rt.annotations?.bold) marks.push({ type: 'bold' });
    if (rt.annotations?.italic) marks.push({ type: 'italic' });
    if (rt.annotations?.underline) marks.push({ type: 'underline' });
    if (rt.annotations?.strikethrough) marks.push({ type: 'strike' });
    if (rt.annotations?.code) marks.push({ type: 'code' });
    if (rt.annotations?.color) {
      marks.push({ type: 'textStyle', attrs: { color: rt.annotations.color } });
    }
    if (rt.annotations?.background_color) {
      marks.push({ type: 'highlight', attrs: { color: rt.annotations.background_color } });
    }
    if (rt.href) {
      marks.push({ type: 'link', attrs: { href: rt.href } });
    }

    const node: JSONContent = {
      type: 'text',
      text: rt.plain_text || '',
    };
    if (marks.length > 0) {
      node.marks = marks;
    }
    return node;
  });
}

/**
 * Convert TipTap JSONContent inline nodes back to our RichText[] format.
 * Expects an array of text nodes (the `content` of a paragraph/heading node).
 */
export function tipTapContentToRichText(content?: JSONContent[]): RichText[] {
  if (!content || content.length === 0) return [];

  return content
    .filter((node) => node.type === 'text' && node.text)
    .map((node) => {
      const rt: RichText = { plain_text: node.text! };
      const marks = node.marks ?? [];

      const hasMark = (type: string) => marks.some((m) => m.type === type);
      const getMarkAttrs = (type: string) => marks.find((m) => m.type === type)?.attrs;

      if (
        hasMark('bold') ||
        hasMark('italic') ||
        hasMark('underline') ||
        hasMark('strike') ||
        hasMark('code') ||
        hasMark('textStyle') ||
        hasMark('highlight')
      ) {
        rt.annotations = {
          bold: hasMark('bold'),
          italic: hasMark('italic'),
          underline: hasMark('underline'),
          strikethrough: hasMark('strike'),
          code: hasMark('code'),
          color: getMarkAttrs('textStyle')?.color,
          background_color: getMarkAttrs('highlight')?.color,
        };
      }

      const linkAttrs = getMarkAttrs('link');
      if (linkAttrs?.href) {
        rt.href = linkAttrs.href;
      }

      return rt;
    });
}

/**
 * Convert a plain text string to a simple RichText array (no formatting).
 */
export function plainTextToRichText(text: string): RichText[] {
  if (!text) return [];
  return [{ plain_text: text }];
}

/**
 * Extract plain text from RichText array.
 */
export function richTextToPlain(richText: RichText[]): string {
  return (richText ?? []).map((rt) => rt.plain_text).join('');
}
